import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';

const STORAGE_KEY = 'inga-elin:wishlist:v1';
const CHANGE_EVENT = 'wishlist:change';
const validHandle = (handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(handle);

function readItems() {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    return items.filter((item) => {
      if (!item || !validHandle(item.handle) || seen.has(item.handle)) return false;
      seen.add(item.handle);
      return true;
    }).slice(0, 100).map(({ handle, title }) => ({ handle, title: typeof title === 'string' ? title.slice(0, 300) : handle }));
  } catch {
    return [];
  }
}

function announce(key) {
  const owner = document.getElementById('wishlist');
  const inDialog = owner?.querySelector('dialog')?.open;
  const region = owner?.querySelector(inDialog ? '[data-wishlist-status]' : '[data-wishlist-announcement]');
  if (region) {
    region.textContent = owner.dataset[key] || '';
    if (!inDialog) region.classList.toggle('visually-hidden', key !== 'storageError' && key !== 'limit');
  }
}

function toggleItem(handle, title) {
  if (!validHandle(handle)) return;
  const items = readItems();
  const saved = items.some((item) => item.handle === handle);
  if (!saved && items.length >= 100) {
    announce('limit');
    return;
  }
  const next = saved ? items.filter((item) => item.handle !== handle) : [...items, { handle, title }];
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    announce('storageError');
    return;
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  announce(saved ? 'removed' : 'added');
}

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY || event.key === null) window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
});

class WishlistToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(CHANGE_EVENT, this.sync);
    this.sync();
  }
  updatedCallback() {
    super.updatedCallback();
    this.sync();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(CHANGE_EVENT, this.sync);
  }
  sync = () => {
    const button = this.querySelector('button');
    if (!button) return;
    const saved = readItems().some((item) => item.handle === this.dataset.handle);
    const label = saved ? this.dataset.remove : this.dataset.add;
    button.disabled = false;
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', `${label}: ${this.dataset.title}`);
    const text = this.querySelector('[data-wishlist-label]');
    if (text) text.textContent = label;
  };
  /** @param {MouseEvent} event */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    toggleItem(this.dataset.handle, this.dataset.title);
  }
}

class WishlistLink extends Component {
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(CHANGE_EVENT, this.sync);
    this.sync();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(CHANGE_EVENT, this.sync);
  }
  sync = () => {
    const count = readItems().length;
    const badge = this.querySelector('[data-wishlist-header-count]');
    const button = this.querySelector('button');
    if (badge) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    }
    if (button) {
      button.disabled = false;
      button.setAttribute('aria-label', `${this.dataset.label}${count ? ` (${count})` : ''}`);
    }
  };
}

class WishlistDialog extends DialogComponent {
  #request;
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(CHANGE_EVENT, this.#onChange);
    this.addEventListener('dialog:close', this.#onClose);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
    window.removeEventListener(CHANGE_EVENT, this.#onChange);
    this.removeEventListener('dialog:close', this.#onClose);
  }
  showDialog() {
    super.showDialog();
    for (const button of document.querySelectorAll('[aria-controls="wishlist-dialog"]')) button.setAttribute('aria-expanded', 'true');
    this.refresh(true);
  }
  #onClose = () => {
    this.#request?.abort();
    for (const button of document.querySelectorAll('[aria-controls="wishlist-dialog"]')) button.setAttribute('aria-expanded', 'false');
  };
  #onChange = () => {
    if (this.refs.dialog.open) this.refresh();
  };

  async refresh(reload = false) {
    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;
    const items = readItems();
    const grid = this.querySelector('[data-wishlist-grid]');
    const status = this.querySelector('[data-wishlist-status]');
    const empty = this.querySelector('[data-wishlist-empty]');
    this.querySelector('[data-wishlist-count]').textContent = items.length ? String(items.length) : '';
    empty.hidden = items.length > 0;
    status.textContent = '';
    const wanted = new Set(items.map((item) => item.handle));
    for (const card of [...grid.children]) {
      if (reload || !wanted.has(card.dataset.wishlistCard)) {
        if (card.contains(document.activeElement)) {
          const next = card.nextElementSibling?.querySelector('button, a') || card.previousElementSibling?.querySelector('button, a');
          (next || this.querySelector('.wishlist-dialog__close')).focus();
        }
        card.remove();
      }
    }
    const pending = [];
    for (const item of items) {
      let card = [...grid.children].find((node) => node.dataset.wishlistCard === item.handle);
      if (card?.dataset.loaded) continue;
      if (!card) {
        card = document.createElement('article');
        card.className = 'wishlist-card';
        card.dataset.wishlistCard = item.handle;
        const label = document.createElement('p');
        label.textContent = item.title;
        card.append(label);
        grid.append(card);
      }
      pending.push({ item, card });
    }
    if (!pending.length) {
      grid.removeAttribute('aria-busy');
      return;
    }
    grid.setAttribute('aria-busy', 'true');
    status.textContent = this.dataset.loading;
    let failed = false;
    const worker = async () => {
      while (pending.length && !controller.signal.aborted) {
        const { item, card } = pending.shift();
        try {
          const root = this.dataset.root.replace(/\/?$/, '/');
          const url = new URL(`${root}products/${encodeURIComponent(item.handle)}`, location.origin);
          const html = await sectionRenderer.getSectionHTML('wishlist-product', false, url, controller.signal);
          if (controller.signal.aborted) return;
          const result = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wishlist-card]');
          if (!result || result.dataset.wishlistCard !== item.handle) throw new Error('Product unavailable');
          result.dataset.loaded = 'true';
          card.replaceWith(result);
        } catch (error) {
          if (controller.signal.aborted) return;
          failed = true;
          this.#renderError(card, item);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, pending.length) }, worker));
    if (!controller.signal.aborted) {
      grid.removeAttribute('aria-busy');
      status.textContent = failed ? this.dataset.error : '';
    }
  }

  #renderError(card, item) {
    card.className = 'wishlist-card wishlist-card--error';
    card.replaceChildren();
    const title = document.createElement('p');
    title.textContent = item.title;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button-secondary';
    retry.textContent = this.dataset.retry;
    retry.addEventListener('click', () => this.refresh());
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'button-unstyled';
    remove.textContent = this.dataset.remove;
    remove.addEventListener('click', () => toggleItem(item.handle, item.title));
    card.append(title, retry, remove);
  }
}

if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);
if (!customElements.get('wishlist-link')) customElements.define('wishlist-link', WishlistLink);
if (!customElements.get('wishlist-dialog')) customElements.define('wishlist-dialog', WishlistDialog);
